import React, { useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Paper, Divider, CircularProgress, Button, Stack,
  Chip, Alert, Dialog, DialogTitle, DialogContent, IconButton
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { fetchPaymentById } from '../slices/financeSlice';
import InfoIcon from '@mui/icons-material/Info';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard'; // NEW: Visa icon
import DescriptionIcon from '@mui/icons-material/Description'; // NEW: Description icon
import { motion } from 'framer-motion';
import { exportToPrintWindow, exportVoucherToPrint } from '../utils/exportUtils';

import { formatPhoneForWhatsApp } from '../utils/phoneUtils';

const MotionPaper = motion(Paper);
const FONT = 'Cairo, Tajawal, sans-serif';
const DS_SHADOW = '0 2px 12px rgba(67,56,202,0.07)';

const ViewPaymentPage = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const payment = useSelector((state) => state.finance.selectedPayment);

  useEffect(() => {
    dispatch(fetchPaymentById(id));
  }, [id, dispatch]);

  const handlePrint = () => {
    exportVoucherToPrint(payment, { type: 'سند صرف' });
  };

  const handleViewChequeImage = () => {
    if (payment?.cheque?.cheque_image) {
      window.open(payment.cheque.cheque_image, '_blank');
    }
  };

  const formatChequeDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateString;
    }
  };

  // Handle WhatsApp message sending
  const handleSendWhatsApp = () => {
    const formattedPhone = payment?.target_phone;
    if (!formattedPhone) {
      showToast('لا يوجد رقم هاتف صحيح للمستفيد', 'error');
      return;
    }

    const buildMessage = (p) => {
      let message = `سند صرف من برنامج دفتر نون:\n` +
        `رقم السند: ${p.number || '-'}\n` +
        `اسم المستفيد: ${p.target_name || p.targetName || '-'}\n` +
        `المبلغ: ${p.amount || '-'} شيكل\n` +
        `طريقة الدفع: ${p.payment_type || p.method || '-'}\n` +
        `التاريخ: ${p.date || '-'}\n` +
        `الوقت: ${p.time_display || p.time || '-'}\n` +
        `نوع الدفعة: ${p.type || '-'}\n` +
        `السبب/البيان: ${p.reason || '-'}\n` +
        `منشئ السند: ${p.created_by_name || '-'}\n`;

      // Add cheque details if payment is by cheque
      if (p.cheque && (p.payment_type === 'شيك' || p.method === 'شيك')) {
        message += `--- تفاصيل الشيك ---\n` +
          `رقم الشيك: ${p.cheque.cheque_number || '-'}\n` +
          `تاريخ الشيك: ${p.cheque.cheque_date || '-'}\n` +
          `اسم البنك: ${p.cheque.bank_name || '-'}\n` +
          `اسم الفرع: ${p.cheque.branch_name || '-'}\n` +
          `رقم البنك: ${p.cheque.bank_number || '-'}\n` +
          `رقم الفرع: ${p.cheque.branch_number || '-'}\n` +
          `رقم الحساب: ${p.cheque.account_number || '-'}\n`;
      }

      // Add visa details if payment is by visa
      if (p.visa && (p.payment_type === 'فيزا' || p.method === 'فيزا')) {
        message += `--- تفاصيل الفيزا ---\n` +
          `رقم الإيصال: ${p.visa.receipt_number || '-'}\n` +
          `الوصف: ${p.visa.description || '-'}\n`;
      }

      return message;
    };

    const url = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(buildMessage(payment))}`;
    window.open(url, '_blank');
  };

  if (!payment) return null;

  const isChequePayment = payment.payment_type === 'شيك' || payment.method === 'شيك';
  const isVisaPayment = payment.payment_type === 'فيزا' || payment.method === 'فيزا';
  const hasPhoneNumber = payment.target_phone && payment.target_phone.trim() !== '';

  // 🎨 Design System Applied
  const sectionLabelSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    fontFamily: FONT,
    fontWeight: 700,
    color: '#1E1B4B',
    fontSize: '1.05rem',
    mb: 2.5,
  };

  const iconTileSx = (bg, color) => ({
    bgcolor: bg,
    color,
    borderRadius: 2,
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 22 },
  });

  const fieldLabelSx = {
    fontFamily: FONT,
    fontWeight: 600,
    color: '#6B7280',
    fontSize: '0.875rem',
    minWidth: 120,
  };

  const fieldValueSx = {
    fontFamily: FONT,
    fontWeight: 700,
    color: '#1E1B4B',
    fontSize: '0.9rem',
  };

  return (
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: FONT }}
              >
                تفاصيل سند الصرف
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: FONT }}
              >
                {payment.number ? `رقم السند: ${payment.number}` : 'عرض تفاصيل سند الصرف المالي'}
              </Typography>
            </Box>
            {isChequePayment && (
              <Chip
                icon={<CheckCircleIcon sx={{ color: '#fff !important' }} />}
                label="دفع بالشيك"
                sx={{
                  fontWeight: 700,
                  fontFamily: FONT,
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.5)',
                }}
              />
            )}
            {isVisaPayment && (
              <Chip
                icon={<CreditCardIcon sx={{ color: '#fff !important' }} />}
                label="دفع بالفيزا"
                sx={{
                  fontWeight: 700,
                  fontFamily: FONT,
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.5)',
                }}
              />
            )}
          </Stack>

          <Stack spacing={1}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.85)', fontFamily: FONT }}
            >
              إجراءات السند
            </Typography>
            <Stack useFlexGap direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.6)',
                  px: 2.5,
                  py: 1,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: FONT,
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
                }}
              >
                طباعة
              </Button>

              {hasPhoneNumber && (
                <Button
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  onClick={handleSendWhatsApp}
                  sx={{
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.6)',
                    px: 2.5,
                    py: 1,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontFamily: FONT,
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  واتساب
                </Button>
              )}

              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/finance/edit/${id}`)}
                sx={{
                  bgcolor: '#fff',
                  color: '#4338CA',
                  px: 2.5,
                  py: 1,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: FONT,
                  '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
                }}
              >
                تعديل
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/financial')}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.6)',
                  px: 2.5,
                  py: 1,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: FONT,
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
                }}
              >
                رجوع
              </Button>
            </Stack>
          </Stack>
        </MotionPaper>

        {/* Main Details */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            p: { xs: 2.5, sm: 3.5 },
            mb: 3,
            bgcolor: '#fff',
            boxShadow: DS_SHADOW,
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography sx={sectionLabelSx}>
            <Box component="span" sx={iconTileSx('#EEF2FF', '#4338CA')}>
              <InfoIcon />
            </Box>
            المعلومات الأساسية
          </Typography>

          <Grid container spacing={2} dir="rtl">
            {payment.number && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>رقم السند:</Typography>
                  <Typography sx={fieldValueSx}>{payment.number}</Typography>
                </Stack>
              </Grid>
            )}
            {payment.amount && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>المبلغ:</Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontWeight: 800,
                      color: '#16A34A',
                      fontSize: '1.15rem',
                      bgcolor: '#DCFCE7',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                    }}
                  >
                    {Number(payment.amount).toLocaleString('en-US')} شيكل
                  </Typography>
                </Stack>
              </Grid>
            )}
            {(payment.payment_type || payment.method) && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>طريقة الدفع:</Typography>
                  <Chip
                    label={payment.payment_type || payment.method}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontFamily: FONT,
                      bgcolor: isChequePayment ? '#FEF3C7' : isVisaPayment ? '#E0F2FE' : '#EEF2FF',
                      color: isChequePayment ? '#D97706' : isVisaPayment ? '#0284C7' : '#4338CA',
                    }}
                  />
                </Stack>
              </Grid>
            )}
            {payment.date && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>التاريخ:</Typography>
                  <Typography sx={fieldValueSx}>{payment.date}</Typography>
                </Stack>
              </Grid>
            )}
            {(payment.time_display || payment.time) && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>الوقت:</Typography>
                  <Typography sx={fieldValueSx}>{payment.time_display || payment.time}</Typography>
                </Stack>
              </Grid>
            )}
            {(payment.target_name || payment.targetName) && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>اسم المستفيد:</Typography>
                  <Typography sx={fieldValueSx}>{payment.target_name || payment.targetName}</Typography>
                </Stack>
              </Grid>
            )}
            {hasPhoneNumber && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>رقم الهاتف:</Typography>
                  <Typography sx={{ ...fieldValueSx, color: '#0284C7' }}>
                    {payment.target_phone}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {payment.type && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>نوع الدفعة:</Typography>
                  <Typography sx={fieldValueSx}>{payment.type}</Typography>
                </Stack>
              </Grid>
            )}
            {payment.reason && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>السبب / البيان:</Typography>
                  <Typography sx={fieldValueSx}>{payment.reason}</Typography>
                </Stack>
              </Grid>
            )}
            {payment.created_by_name && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={fieldLabelSx}>منشئ السند:</Typography>
                  <Chip
                    icon={<AccountCircleIcon sx={{ color: '#0284C7 !important' }} />}
                    label={payment.created_by_name}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontFamily: FONT,
                      bgcolor: '#E0F2FE',
                      color: '#0284C7',
                    }}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </MotionPaper>

        {/* Cheque Details Section - Only show if payment is by cheque */}
        {isChequePayment && payment.cheque && (
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
            sx={{
              borderRadius: 3,
              p: { xs: 2.5, sm: 3.5 },
              mb: 3,
              bgcolor: '#fff',
              boxShadow: DS_SHADOW,
              border: '1px solid #E5E7EB',
            }}
          >
            <Typography sx={sectionLabelSx}>
              تفاصيل الشيك
            </Typography>

            <Grid container spacing={3} dir="rtl">
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  {payment.cheque.bank_name && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ ...fieldLabelSx, minWidth: 100 }}>اسم البنك:</Typography>
                      <Typography sx={fieldValueSx}>{payment.cheque.bank_name}</Typography>
                    </Stack>
                  )}

                  {payment.cheque.branch_name && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ ...fieldLabelSx, minWidth: 100 }}>اسم الفرع:</Typography>
                      <Typography sx={fieldValueSx}>{payment.cheque.branch_name}</Typography>
                    </Stack>
                  )}

                  {payment.cheque.bank_number && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ ...fieldLabelSx, minWidth: 100 }}>رقم البنك:</Typography>
                      <Typography sx={fieldValueSx}>{payment.cheque.bank_number}</Typography>
                    </Stack>
                  )}

                  {payment.cheque.branch_number && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ ...fieldLabelSx, minWidth: 100 }}>رقم الفرع:</Typography>
                      <Typography sx={fieldValueSx}>{payment.cheque.branch_number}</Typography>
                    </Stack>
                  )}

                  {payment.cheque.account_number && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ ...fieldLabelSx, minWidth: 100 }}>رقم الحساب:</Typography>
                      <Typography sx={fieldValueSx}>{payment.cheque.account_number}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  {payment.cheque.cheque_number && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ ...fieldLabelSx, minWidth: 100 }}>رقم الشيك:</Typography>
                      <Typography sx={fieldValueSx}>{payment.cheque.cheque_number}</Typography>
                    </Stack>
                  )}

                  {payment.cheque.cheque_date && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ ...fieldLabelSx, minWidth: 100 }}>تاريخ الاستحقاق:</Typography>
                      <Typography sx={fieldValueSx}>{formatChequeDate(payment.cheque.cheque_date)}</Typography>
                    </Stack>
                  )}

                  {payment.cheque.cheque_image && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ ...fieldLabelSx, minWidth: 100 }}>صورة الشيك:</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ImageIcon />}
                        onClick={handleViewChequeImage}
                        sx={{
                          borderRadius: 2.5,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontFamily: FONT,
                          borderColor: '#4338CA',
                          color: '#4338CA',
                          '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
                        }}
                      >
                        عرض الصورة
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </MotionPaper>
        )}

        {/* Visa Details Section - Only show if payment is by visa */}
        {isVisaPayment && payment.visa && (
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
            sx={{
              borderRadius: 3,
              p: { xs: 2.5, sm: 3.5 },
              mb: 3,
              bgcolor: '#fff',
              boxShadow: DS_SHADOW,
              border: '1px solid #E5E7EB',
            }}
          >
            <Typography sx={sectionLabelSx}>
              <Box component="span" sx={iconTileSx('#E0F2FE', '#0284C7')}>
                <CreditCardIcon />
              </Box>
              تفاصيل الفيزا
            </Typography>

            <Grid container spacing={3} dir="rtl">
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  {payment.visa.receipt_number && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Box component="span" sx={iconTileSx('#E0F2FE', '#0284C7')}>
                        <ReceiptIcon />
                      </Box>
                      <Typography sx={fieldLabelSx}>رقم الإيصال:</Typography>
                      <Typography sx={fieldValueSx}>{payment.visa.receipt_number}</Typography>
                    </Stack>
                  )}

                  {payment.visa.description && (
                    <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                      <Box component="span" sx={iconTileSx('#EEF2FF', '#4338CA')}>
                        <DescriptionIcon />
                      </Box>
                      <Typography sx={fieldLabelSx}>الوصف:</Typography>
                      <Typography sx={fieldValueSx}>{payment.visa.description}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </MotionPaper>
        )}

      </Box>
    </Box>
  );
};

export default ViewPaymentPage;